import { Loading } from "@msuite/ui/loading-screen";
import { useTranslation } from "@msuite/ui/localization";
import { useQuery } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import type { PropsWithChildren } from "react";
import { PreCheckBlankSlate } from "@/components/integrations/pre-check-blank-slate";
import { client } from "@/hono";
import { useCitadel } from "@/hooks/use-citadel";

/** Props Interface */
interface IntegrationPreCheckProps extends PropsWithChildren {
	integrationType: "personio" | "webfleet";
}

export function IntegrationPreCheck({ integrationType, children }: IntegrationPreCheckProps) {
	/** Hooks */
	const { t } = useTranslation();
	const { citadel, isError: rulesFailed } = useCitadel();

	/** Computed */
	// Three states, not two. `citadel` is undefined while the caller's rules are still being
	// fetched and again when the fetch failed, and neither of those is a refusal — answering
	// "no access" to somebody whose rules simply have not arrived tells them something untrue
	// about their own permissions and sends them to an administrator over a network blip.
	const areRulesLoading = !citadel && !rulesFailed;

	// Whether an integration exists is `read Integration`, which the rule opening this screen —
	// `create User` on the people list, `create Vehicle` on the fleet — does not imply. Without it
	// the check says so instead of firing a request whose refusal would arrive as a toast.
	const canReadIntegration = citadel?.can("read", "Integration") === true;

	/** Query */
	const { data, isLoading } = useQuery({
		queryKey: [integrationType, "integration"],
		queryFn: async () => {
			const response = await client.integrations[":integrationType"].state.$get({
				param: { integrationType },
			});
			const data = await responseHandler(response);
			return data;
		},
		enabled: canReadIntegration,
	});

	/** Guard */
	if (areRulesLoading) {
		return (
			<Loading
				isLoading
				label={t("general.checking_permissions")}
			/>
		);
	}

	if (rulesFailed) {
		return <PreCheckBlankSlate label={t("general.permissions_unavailable")} />;
	}

	if (!canReadIntegration) {
		return <PreCheckBlankSlate label={t("general.no_access")} />;
	}

	/** Render */
	return (
		<Loading
			isLoading={isLoading}
			label={t("integrations.checking_active_integration")}
		>
			{data?.hasIntegration ? (
				children
			) : (
				<PreCheckBlankSlate label={t("integrations.integration_not_active")} />
			)}
		</Loading>
	);
}
